import { formatDistanceToNow, parseISO } from 'date-fns';
import { StatusBadge } from '../common/StatusBadge';
import type { Job, JobRun } from '../../types/jobs';

interface JobCardProps {
  job: Job;
  lastRun: JobRun | undefined;
  onClick: () => void;
}

export function formatSchedule(job: Job): string {
  if (job.schedule_type === 'interval') {
    const minutes = parseInt(job.schedule_expr, 10);
    if (!isNaN(minutes)) {
      return minutes >= 60 ? `Every ${minutes / 60}h` : `Every ${minutes} min`;
    }
    return `Every ${job.schedule_expr}`;
  }

  // Parse cron: min hour dom month dow
  const parts = job.schedule_expr.split(/\s+/);
  if (parts.length < 5) return job.schedule_expr;

  const [min, hour, dom, , dow] = parts;

  const formatTime = (h: string, m: string): string => {
    const hr = parseInt(h, 10);
    const mn = parseInt(m, 10);
    const ampm = hr >= 12 ? 'PM' : 'AM';
    const displayHr = hr === 0 ? 12 : hr > 12 ? hr - 12 : hr;
    return mn === 0 ? `${displayHr}:00 ${ampm}` : `${displayHr}:${String(mn).padStart(2, '0')} ${ampm}`;
  };

  // Multiple times per day (e.g., */6 * * * *)
  if (hour.includes('/')) {
    const interval = parseInt(hour.split('/')[1], 10);
    return `Every ${interval} hours`;
  }

  // Specific day of month
  if (dom !== '*') {
    const suffix =
      dom === '1' ? 'st' : dom === '2' ? 'nd' : dom === '3' ? 'rd' : 'th';
    return `${dom}${suffix} at ${formatTime(hour, min)}`;
  }

  // Specific day of week
  if (dow !== '*') {
    const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const dayName = DAYS[parseInt(dow, 10)] ?? dow;
    return `${dayName} at ${formatTime(hour, min)}`;
  }

  // Daily
  return `Daily at ${formatTime(hour, min)}`;
}

export function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const totalSec = Math.round(ms / 1000);
  if (totalSec < 60) return `${totalSec}s`;
  const minutes = Math.floor(totalSec / 60);
  const seconds = totalSec % 60;
  return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
}

export default function JobCard({ job, lastRun, onClick }: JobCardProps) {
  const isRunning = lastRun?.status === 'running';

  return (
    <div
      className={`bg-bg-secondary border border-border-subtle rounded-lg p-3 hover:border-border-hover cursor-pointer transition-colors ${
        isRunning ? 'border-l-2 border-l-accent-purple' : ''
      }`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <div className="flex items-center justify-between mb-1.5">
        <span className="font-medium text-text-primary text-sm truncate">{job.name}</span>
        {lastRun ? <StatusBadge status={lastRun.status} size="sm" /> : null}
      </div>

      <p className="text-xs text-text-secondary mb-2">{formatSchedule(job)}</p>

      {lastRun ? (
        <div className="flex items-center gap-3 text-xs text-text-muted">
          <span>{formatDistanceToNow(parseISO(lastRun.started_at), { addSuffix: true })}</span>
          {lastRun.duration_ms != null ? (
            <span>{formatDuration(lastRun.duration_ms)}</span>
          ) : null}
          {lastRun.cost_usd > 0 ? <span>${lastRun.cost_usd.toFixed(2)}</span> : null}
        </div>
      ) : (
        <p className="text-xs text-text-muted">No runs yet</p>
      )}
    </div>
  );
}
